import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Menu, X, Sun, Moon, Globe, User, LogOut, Shield, MessageCircle, ShoppingBag, Home, Smartphone, UserCircle, MessageSquare } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { canInstallPWA, installPWA, isStandalone } from '../utils/pwa';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/DropdownMenu';
export function Navbar() {
  const {
    t,
    language,
    setLanguage
  } = useLanguage();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    currentUser,
    isAdmin,
    logout
  } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  useEffect(() => {
    const check = () => setCanInstall(canInstallPWA() && !isStandalone());
    check();
    window.addEventListener('beforeinstallprompt', check);
    return () => window.removeEventListener('beforeinstallprompt', check);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path: string) => location.pathname === path;
  const navLinks = [{
    path: '/',
    label: t('nav.home'),
    icon: Home
  }, {
    path: '/marketplace',
    label: t('nav.marketplace'),
    icon: ShoppingBag
  }, ...(currentUser ? [{
    path: '/profile',
    label: 'Profile',
    icon: UserCircle
  }, {
    path: '/chat',
    label: t('nav.chat'),
    icon: MessageCircle
  }] : []), ...(isAdmin ? [{
    path: '/admin/dashboard',
    label: t('nav.admin'),
    icon: Shield
  }] : [])];
  return <nav className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-id="element-110">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-111">
        <div className="flex h-16 items-center justify-between" data-id="element-112">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 font-heading font-bold text-lg text-primary group" data-id="element-113">
            <motion.div whileHover={{
            rotate: [0, -10, 10, 0]
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-114">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md shadow-blue-600/20" data-id="element-115">
                <GraduationCap className="h-4.5 w-4.5 text-white" data-id="element-116" />
              </div>
              <div className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-400 border-2 border-background" data-id="element-117" />
            </motion.div>
            <div className="hidden sm:flex flex-col leading-none" data-id="element-118">
              <span className="text-sm font-bold tracking-tight" data-id="element-119">
                Theo EduMarket
              </span>
              <span className="text-[9px] text-muted-foreground font-medium tracking-wider uppercase" data-id="element-120">
                Rwanda
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1" data-id="element-121">
            {navLinks.map(link => <Link key={link.path} to={link.path} data-id="element-122">
                <Button variant={isActive(link.path) ? 'default' : 'ghost'} size="sm" className="gap-2" data-id="element-123">
                  <link.icon className="h-4 w-4" data-id="element-124" />
                  {link.label}
                </Button>
              </Link>)}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2" data-id="element-125">
            {/* WhatsApp */}
            <a href="https://wa.me/250783138128?text=Hello%20Theo%20EduMarket%20Rwanda" target="_blank" rel="noopener noreferrer" data-id="element-126">
              <Button variant="ghost" size="icon-sm" className="text-green-500 hover:text-green-600" data-id="element-127">
                <MessageSquare className="h-4 w-4" data-id="element-128" />
              </Button>
            </a>

            {/* Install PWA */}
            {canInstall && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} data-id="element-129">
                <Button variant="ghost" size="icon-sm" className="text-blue-600" onClick={async () => {
              const ok = await installPWA();
              if (ok) toast.success('App installed!');
              setCanInstall(false);
            }} data-id="element-130">
                  <Smartphone className="h-4 w-4" data-id="element-131" />
                </Button>
              </motion.div>}

            {/* Language */}
            <DropdownMenu data-id="element-132">
              <DropdownMenuTrigger asChild data-id="element-133">
                <Button variant="ghost" size="icon-sm" data-id="element-134">
                  <Globe className="h-4 w-4" data-id="element-135" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-id="element-136">
                <DropdownMenuItem onClick={() => setLanguage('en')} className={language === 'en' ? 'bg-accent' : ''} data-id="element-137">
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('kn')} className={language === 'kn' ? 'bg-accent' : ''} data-id="element-138">
                  Ikinyarwanda
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme */}
            <Button variant="ghost" size="icon-sm" onClick={toggleTheme} data-id="element-139">
              {theme === 'light' ? <Moon className="h-4 w-4" data-id="element-140" /> : <Sun className="h-4 w-4" data-id="element-141" />}
            </Button>

            {/* User Menu */}
            {currentUser ? <DropdownMenu data-id="element-142">
                <DropdownMenuTrigger asChild data-id="element-143">
                  <Button variant="ghost" size="sm" className="gap-2" data-id="element-144">
                    <User className="h-4 w-4" data-id="element-145" />
                    <span className="hidden sm:inline max-w-[100px] truncate" data-id="element-146">
                      {currentUser.fullName}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-id="element-147">
                  <div className="px-2 py-1.5 text-sm font-medium" data-id="element-148">
                    {currentUser.fullName}
                  </div>
                  <div className="px-2 pb-1.5 text-xs text-muted-foreground" data-id="element-149">
                    {currentUser.countryCode}
                    {currentUser.phone}
                  </div>
                  <DropdownMenuSeparator data-id="element-150" />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-id="element-151">
                    <UserCircle className="h-4 w-4 mr-2" data-id="element-152" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/chat')} data-id="element-153">
                    <MessageCircle className="h-4 w-4 mr-2" data-id="element-154" />
                    {t('nav.chat')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator data-id="element-155" />
                  <DropdownMenuItem onClick={() => {
                logout();
                navigate('/');
              }} data-id="element-156">
                    <LogOut className="h-4 w-4 mr-2" data-id="element-157" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> : isAdmin ? <DropdownMenu data-id="element-158">
                <DropdownMenuTrigger asChild data-id="element-159">
                  <Button variant="ghost" size="sm" className="gap-2" data-id="element-160">
                    <Shield className="h-4 w-4 text-blue-600" data-id="element-161" />
                    <span className="hidden sm:inline" data-id="element-162">Admin</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-id="element-163">
                  <div className="px-2 py-1.5 text-sm font-medium" data-id="element-164">
                    BAYAVUGE THEOGEN
                  </div>
                  <div className="px-2 pb-1.5 text-xs text-muted-foreground" data-id="element-165">
                    Admin
                  </div>
                  <DropdownMenuSeparator data-id="element-166" />
                  <DropdownMenuItem onClick={() => {
                logout();
                navigate('/');
              }} data-id="element-167">
                    <LogOut className="h-4 w-4 mr-2" data-id="element-168" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> : <Link to="/auth" data-id="element-169">
                <Button size="sm" className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-id="element-170">
                  <User className="h-4 w-4" data-id="element-171" />
                  <span className="hidden sm:inline" data-id="element-172">{t('nav.login')}</span>
                </Button>
              </Link>}

            {/* Mobile Menu Toggle */}
            <Button variant="ghost" size="icon-sm" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-id="element-173">
              {mobileOpen ? <X className="h-5 w-5" data-id="element-174" /> : <Menu className="h-5 w-5" data-id="element-175" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-176">
        {mobileOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="md:hidden border-t border-border overflow-hidden bg-background" data-id="element-177">
            <div className="px-4 py-3 space-y-1" data-id="element-178">
              {navLinks.map(link => <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)} data-id="element-179">
                  <Button variant={isActive(link.path) ? 'default' : 'ghost'} className="w-full justify-start gap-2" size="sm" data-id="element-180">
                    <link.icon className="h-4 w-4" data-id="element-181" />
                    {link.label}
                  </Button>
                </Link>)}
              {!currentUser && !isAdmin && <Link to="/auth" onClick={() => setMobileOpen(false)} data-id="element-182">
                  <Button className="w-full justify-start gap-2 bg-blue-600 hover:bg-blue-700 text-white" size="sm" data-id="element-183">
                    <User className="h-4 w-4" data-id="element-184" />
                    {t('nav.login')} / {t('nav.signup')}
                  </Button>
                </Link>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}